<script setup lang="ts">
import IconMaterialSymbolsLightHandshakeSharp from '~icons/material-symbols-light/handshake-outline-sharp';
import IconMaterialSymbolsLightVerifiedUser from '~icons/material-symbols-light/verified-user-outline';
import IconMaterialSymbolsLightVilla from '~icons/material-symbols-light/villa-outline';
import IconMaterialSymbolsNorthEastRounded from '~icons/material-symbols/north-east-rounded';
import IconSkewedRectangle from '~icons/pilar/skewed-rectangle.svg';

interface Feature {
	title: string;
	description: string;
}

interface Props {
	title: string;
	description: string;
	features: {
		portfolio: Feature;
		connection: Feature;
		secureExperience: Feature;
	};
	cta: string;
}

defineProps<Props>();
const emits = defineEmits<{
	(e: 'searchProperties'): void;
}>();
</script>

<template>
	<section class="bg-secondary">
		<Container class="py-11 md:py-[60px]">
			<div class="flex flex-col gap-10 md:gap-16">
				<div class="flex flex-col gap-y-3">
					<h6
						class="text-center font-matter-sq text-2xl font-normal leading-[150%] text-primary lg:text-4xl lg:tracking-[-0.72px]"
					>
						{{ title }}
					</h6>
					<p
						class="self-stretch text-center text-base font-normal leading-[175%] text-primary lg:text-lg xl:px-40 2xl:px-72"
					>
						{{ description }}
					</p>
				</div>
				<div class="flex flex-col gap-8 text-primary md:flex-row md:gap-9">
					<div class="flex gap-x-6 md:flex-col">
						<div class="relative">
							<IconSkewedRectangle
								class="absolute inset-2 left-0 z-10 h-[16px] w-[40px] fill-beige md:inset-3 md:left-0.5 md:h-[25px] md:w-[60px]"
							/>
							<IconMaterialSymbolsLightVilla
								class="relative z-20 size-8 md:size-12"
							/>
						</div>
						<div>
							<p
								class="self-stretch font-matter-sq text-lg font-normal leading-[150%] tracking-[-0.4px]"
							>
								{{ features.portfolio.title }}
							</p>
							<p
								class="text-sm font-normal leading-[150%] text-primary-light-3"
							>
								{{ features.portfolio.description }}
							</p>
						</div>
					</div>
					<div class="flex gap-x-6 md:flex-col">
						<div class="relative z-0">
							<IconSkewedRectangle
								class="absolute inset-2 left-0 z-10 h-[16px] w-[40px] fill-beige opacity-[0.99] md:inset-3 md:-left-0.5 md:h-[25px] md:w-[60px]"
							/>
							<IconMaterialSymbolsLightHandshakeSharp
								class="relative z-20 size-8 md:size-12"
							/>
						</div>
						<div>
							<p
								class="self-stretch font-matter-sq text-lg font-normal leading-[150%] tracking-[-0.4px]"
							>
								{{ features.connection.title }}
							</p>
							<p
								class="text-sm font-normal leading-[150%] text-primary-light-3"
							>
								{{ features.connection.description }}
							</p>
						</div>
					</div>
					<div class="flex gap-x-6 md:flex-col">
						<div class="relative z-10">
							<IconSkewedRectangle
								class="absolute inset-2 left-0 z-10 h-[16px] w-[40px] fill-beige md:inset-3 md:left-0.5 md:h-[25px] md:w-[60px]"
							/>
							<IconMaterialSymbolsLightVerifiedUser
								class="relative z-20 size-8 md:size-12"
							/>
						</div>
						<div>
							<p
								class="self-stretch font-matter-sq text-lg font-normal leading-[150%] tracking-[-0.4px]"
							>
								{{ features.secureExperience.title }}
							</p>
							<p
								class="text-sm font-normal leading-[150%] text-primary-light-3"
							>
								{{ features.secureExperience.description }}
							</p>
						</div>
					</div>
				</div>
				<div class="flex justify-start md:justify-center">
					<NuxtLink
						v-slot="{ href }"
						custom
						to="/venda/imoveis/sao-paulo-sp-brasil"
					>
						<Button
							as="a"
							:href="href"
							variant="primary"
							class="flex max-h-[44px] max-w-[161px] gap-2 px-4 text-sm"
							@click="emits('searchProperties')"
						>
							<span>{{ cta }}</span>
							<IconMaterialSymbolsNorthEastRounded
								class="size-6 fill-primary"
							/>
						</Button>
					</NuxtLink>
				</div>
			</div>
		</Container>
	</section>
</template>
